import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Typography } from '@components/Typography';
import ContactDeleteModal from '@features/ContactDeleteModal';
import ContactsMergeModal from '@features/ContactsMergeModal';
import EditContactModal from '@features/EditContactModal';
import ContactInfoComponent from '@features/ContactInfo';
import InitConversation from '@features/InitConversation';
import ConversationDetails from './ConversationDetails';
import Avatar from '@components/Avatar';
import { getInitialsParts, getFullName, getUserContacts } from '@helpers/userName';
import { useTranslation } from '@hooks';
import { UserIcon } from 'assets/images/icons';

const StyledUserIcon = styled(UserIcon)({
    width: '14px !important',
    height: '14px !important',
    '& path': {
        fill: 'var(--text-tertiary)',
        fillOpacity: 1,
    },
});

const StyledDrawer = styled(Drawer)(() => ({
    width: '360px',
    zIndex: 2,
    '@media (max-width: 1024px)': {
        zIndex: 1200,
    },
    '& .MuiDrawer-paper': {
        borderLeft: '1px solid #E4EAEB',
        display: 'flex',
        flexDirection: 'column',
        width: '360px',
        top: 0,
        height: '100%',
        right: 0,
        overflowY: 'unset',
        '@media (max-width: 1480px)': {
            width: '270px',
        },
        '@media (max-width: 1280px)': {
            width: '200px',
        },
        '@media (max-width: 1024px)': {
            width: '100%',
        },
    },
}));

// Fixed height matching the thread header (13px page offset + 57px row) so the
// two bottom borders stay level regardless of the header's content mode.
const Header = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    height: 70,
    flex: 'none',
    boxSizing: 'border-box',
    padding: '0 11px 0 16px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    '@media (min-width: 1025px) and (max-width: 1280px)': {
        padding: '0 8px',
    },
    '@media (max-width: 1024px)': {
        height: 'auto',
        padding: '12px 16px',
    },
});

const Content = styled('div')({
    padding: 16,
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    '@media (min-width: 1025px) and (max-width: 1280px)': {
        padding: 8,
    },
});

// Compact Chatwoot-style contact card: the essentials plus a path to the full contact
const ContactCard = styled('div')({
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    paddingBottom: 16,
    marginBottom: 16,
});

const ContactRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
    minWidth: 0,
    '& svg': {
        fontSize: 16,
        color: 'var(--text-tertiary)',
        flex: 'none',
    },
});

const ViewFullContact = styled(Typography)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    color: 'var(--primary-main)',
    cursor: 'pointer',
    '&:hover': {
        textDecoration: 'underline',
    },
});

const StyledCloseIcon = styled(CloseIcon)({
    fill: 'var(--text-secondary)',
    cursor: 'pointer',
    marginLeft: 'auto',
});

const ContactInfo = ({
    open,
    onClose,
    activeConversation = null,
    filterRef = null,
    setActiveConversation = null,
    onDeleteCallback,
    onMergeCallback,
    onUpdateCallback,
    onInitConversationCallback,
}) => {
    const [showFullContact, setShowFullContact] = useState(false);

    const { contact, contactMergeModal, contactDeleteModal, contactEditModal, contactInitConversationModal } =
        useSelector(state => state.contacts);

    const { t } = useTranslation();

    useEffect(() => {
        setShowFullContact(false);
    }, [open, activeConversation?.id]);

    const showConversation = activeConversation && filterRef;
    const contactName = getUserContacts(
        {
            firstName: contact?.firstName,
            lastName: contact?.lastName,
            phoneNumber: contact?.phoneNumber,
            email: contact?.email,
            externalId: contact?.externalId,
        },
        '-',
    );

    return (
        <>
            <StyledDrawer variant="persistent" anchor="right" open={open}>
                <Header>
                    {showFullContact ? (
                        <IconButton size="small" onClick={() => setShowFullContact(false)}>
                            <ArrowBackIcon sx={{ fontSize: 20, fill: 'var(--text-secondary)' }} />
                        </IconButton>
                    ) : (
                        <Avatar
                            size="medium"
                            fullName={getFullName(contact?.firstName, contact?.lastName)}
                            label={getInitialsParts(contact?.firstName, contact?.lastName)}
                            icon={<StyledUserIcon />}
                        />
                    )}
                    <Typography
                        variant="h6"
                        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        {contactName}
                    </Typography>
                    <StyledCloseIcon onClick={onClose} />
                </Header>
                <Content>
                    {showFullContact ? (
                        <ContactInfoComponent
                            contact={contact}
                            resizable
                            setActiveConversation={setActiveConversation}
                        />
                    ) : (
                        <>
                            <ContactCard>
                                {contact?.phoneNumber && (
                                    <ContactRow>
                                        <LocalPhoneOutlinedIcon />
                                        <Typography variant="body2">{contact.phoneNumber}</Typography>
                                    </ContactRow>
                                )}
                                {contact?.email && (
                                    <ContactRow>
                                        <EmailOutlinedIcon />
                                        <Typography
                                            variant="body2"
                                            sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                                        >
                                            {contact.email}
                                        </Typography>
                                    </ContactRow>
                                )}
                                <ViewFullContact variant="caption" onClick={() => setShowFullContact(true)}>
                                    {t('InboxPage.viewFullContact')}
                                    <ArrowForwardRoundedIcon sx={{ fontSize: 14 }} />
                                </ViewFullContact>
                            </ContactCard>
                            {showConversation && (
                                <ConversationDetails
                                    activeConversation={activeConversation}
                                    setActiveConversation={setActiveConversation}
                                    filterRef={filterRef}
                                />
                            )}
                        </>
                    )}
                </Content>
            </StyledDrawer>
            {contactDeleteModal ? (
                <ContactDeleteModal onDeleteCallback={onDeleteCallback} open={contactDeleteModal} />
            ) : null}
            {contactMergeModal ? (
                <ContactsMergeModal onMergeCallback={onMergeCallback} open={contactMergeModal} />
            ) : null}
            {contactEditModal.open ? (
                <EditContactModal onUpdateCallback={onUpdateCallback} modal={contactEditModal} />
            ) : null}
            {contactInitConversationModal.open ? (
                <InitConversation onInitConversationCallback={onInitConversationCallback} />
            ) : null}
        </>
    );
};

export default ContactInfo;
